import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';

import { apiQuery } from '../../query';

import { PaymentMethod } from './types';
import { applyPaymentMethodOrder } from './helpers';

const tagTypes = ['payment-methods'];

export const paymentMethodsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'paymentMethodsApi',
  endpoints: (build) => ({
    edit: build.mutation({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'put',
        url: URL.PAYMENT_METHOD.replace(':id', data.id),
      }),
    }),
    list: build.query<PaymentMethod[], Pick<PaymentMethod, 'status'>>({
      providesTags: tagTypes,
      query: (query) => ({
        method: 'get',
        url: addParamsToUrl(URL.PAYMENT_METHODS, query),
      }),
    }),
    saveAll: build.mutation<void, Partial<PaymentMethod>[]>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        method: 'put',
        url: URL.PAYMENT_METHODS,
        data: {
          data: JSON.stringify(data.filter((item) => item.name)),
        },
      }),
    }),
    // Company Owner only. Every cached list shows the new order at once and
    // goes back if the save is refused. No refetch: the settings page swaps
    // its form for a loader while fetching, which would drop focus and edits.
    reorder: build.mutation<void, number[]>({
      query: (ids) => ({
        method: 'put',
        data: { ids },
        url: URL.PAYMENT_METHODS_ORDER,
      }),
      async onQueryStarted(ids, { dispatch, getState, queryFulfilled }) {
        const patches = paymentMethodsApi.util
          .selectCachedArgsForQuery(getState(), 'list')
          .map((args) =>
            dispatch(
              paymentMethodsApi.util.updateQueryData('list', args, (draft) =>
                applyPaymentMethodOrder(draft, ids)
              )
            )
          );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

export const {
  useListQuery,
  useEditMutation,
  useReorderMutation,
  useSaveAllMutation,
} = paymentMethodsApi;
export { movePaymentMethod, applyPaymentMethodOrder } from './helpers';
export type { PaymentMethod };
